/**
 * @file Base grid bar component.
 * @author Wim Haanstra
 */
import * as tslib_1 from "tslib";
import { OnInit, ElementRef, NgZone, EventEmitter } from '@angular/core';
import { ControlBase } from '../../base/helpers/control-base';
import { GridDisplayBaseComponent } from '../grid-display-base/grid-display-base.component';
import { FormControl } from '@angular/forms';
import * as moment from 'moment';
var GridBarBaseComponent = /** @class */ (function (_super) {
    tslib_1.__extends(GridBarBaseComponent, _super);
    /**
     * Creates an instance of GridBarBaseComponent.
     */
    function GridBarBaseComponent(executorsService, alertsService, elementRef, ngZone, eventsService, gridService) {
        var _this = _super.call(this, alertsService) || this;
        _this.executorsService = executorsService;
        _this.alertsService = alertsService;
        _this.elementRef = elementRef;
        _this.ngZone = ngZone;
        _this.gridService = gridService;
        _this.gridBarControl = new FormControl();
        /**
         * The index of the currently expanded row
         * @type {number}
         */
        _this.expandedIndex = null;
        _this.expandGridChanged = eventsService.register('expand-grid-changed');
        return _this;
    }
    Object.defineProperty(GridBarBaseComponent.prototype, "visibleHourStart", {
        /**
         * The start hour of the day the component is using
         * This is fetched from the settings
         * @readonly
         * @type {number}
         */
        get: function () {
            return this.gridService.visibleHoursStart;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GridBarBaseComponent.prototype, "visibleHoursEnd", {
        /**
         * The end hour of the day the component is using
         * This is fetched from the settings
         * @readonly
         * @type {number}
         */
        get: function () {
            return this.gridService.visibleHoursEnd;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GridBarBaseComponent.prototype, "visibleDays", {
        /**
         * Day numbers that should be displayed
         * Sunday = 0, monday = 1, ...
         * These days are fetched from the settings and dependant on the 'weekend' checkbox
         * @readonly
         * @type {number}
         */
        get: function () {
            return this.gridService.visibleDays;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Mouse enters an item
     * @param event The event
     */
    GridBarBaseComponent.prototype.mouseEnterItem = function (index) {
        var _this = this;
        this.expandedIndex = index;
        this.expandGridChanged.emit(true);
        this.autoStopExpanding = window.setTimeout(function () {
            _this.mouseLeaveItem(index);
        }, 8000);
    };
    /**
     * Mouse leaves an item
     * @param event The event
     */
    GridBarBaseComponent.prototype.mouseLeaveItem = function (index) {
        clearTimeout(this.autoStopExpanding);
        this.expandedIndex = null;
        this.expandGridChanged.emit(true);
    };
    /**
     * Used to improve the performances of the grid bar by introducing
     * an unique identifier per resource.
     * @param {number} index The index number
     * @param {Entity} item The item for given row
     * @returns {String} The unique identifier
     */
    GridBarBaseComponent.prototype.trackByResource = function (index, item) {
        return (item['Entity'] && item['Entity']['Oid']) ? item['Entity']['Oid'] : index;
    };
    /**
     * Handling the drop event
     * @param {DragEvent} event
     */
    GridBarBaseComponent.prototype.dropItem = function (event, data) {
        var _this = this;
        // Prevent the default handling of the element
        event.preventDefault();
        // Set the drop component
        data.dropComponent = this;
        // Create the (empty) targets array
        data.targets = [];
        var gridCollections = this.griddisplay.gridCollections;
        if (gridCollections) {
            // Find all the data related to given resource
            var matchedGridSegments = gridCollections.find(function (gridSegments) {
                return (gridSegments.meta.resource === data.resources[0]);
            });
            // Collect the grid columns in this nested structure
            if (matchedGridSegments) {
                matchedGridSegments.children.forEach(function (gridSegment) {
                    gridSegment.children.forEach(function (gridRows) {
                        gridRows.children.forEach(function (gridColumn) {
                            // Create a timeslots for the executor
                            data.targets.push({
                                start: gridColumn.meta.start,
                                finish: gridColumn.meta.finish
                            });
                        });
                    });
                });
            }
        }
        // Get the executor id from the drag data
        /* Fix issue: Xerius: Bij aanlog via IE is het niet mogelijk om Afwezigheden /Services te droppen op Planbord #2718
         * Oplossing gebruik alleen 'Text' bij dataTransfer.
         * Zie: https://stackoverflow.com/questions/26213011/html5-dragdrop-issue-in-internet-explorer-datatransfer-property-access-not-pos
         */
        var executorId = event.dataTransfer.getData('Text');
        // Use the executor id to find the executor event
        var executorEvent = this.executorsService.getExecutorEventById(executorId);
        // Merge executor data drag (often from the dropbar) and executor data drop (often a timeslot)
        var mergedData = Object.assign(executorEvent.data, data);
        // Get the executor
        this.executorsService.getExecutorByName(executorEvent.executorName).then(function (executor) {
            // Move is not supported on a resource
            if (executor.action === 'move') {
                // Stop the execution
                return _this.alertsService.createAlert('warning', 'APP.EXECUTORS.NO_MOVE_TO_RESOURCE');
            }
            // Drops from the plugins (are Services) is not supported
            if (executor.scopes.indexOf('Service') !== -1) {
                return _this.alertsService.createAlert('warning', 'APP.EXECUTORS.NO_DROP_FROM_PLUGIN');
            }
            // Execute the executor
            return executor.execute(mergedData).then(function (_a) {
                var items = _a[0], successMessage = _a[1];
                // Execution is successful
                _this.alertsService.createAlert('success', successMessage);
                _this.griddisplay.refreshWith(items);
            });
        }).catch(function (error) {
            // Something went wrong
            if (error.entityErrors && error.entityErrors.length > 0) {
                // Error alert already created by executor.
                return;
            }
            _this.alertsService.createAlert('danger', (typeof error === 'string') ? error : 'APP.EXECUTORS.EXECUTION_FAILED', (typeof error === 'string') ? undefined : error);
        });
    };
    /**
     * Is called during the initialization of the component
     */
    GridBarBaseComponent.prototype.ngOnInit = function () {
        var _this = this;
        // The dragover event is needed to make a drop work. The problem is that for every move the draggable
        // item fires the dragover event of Angular and a change detection is handled.  This is per pixel (!)
        // so it's quite slow to 're-render' the grid. Therefore I placed the dragover event outside Angular via NgZone.
        // This construction is used in grid-display-base.component.ts, grid-drag-base.component.ts, plugin-view-shifts and grid-bar-base.component.ts.
        this.ngZone.runOutsideAngular(function () {
            _this.elementRef.nativeElement.addEventListener('dragover', function (event) {
                event.stopPropagation();
                event.preventDefault();
            });
            _this.elementRef.nativeElement.addEventListener('drop', function (event) {
                event.stopPropagation();
                event.preventDefault();
            });
        });
    };
    /** Is given date today. */
    GridBarBaseComponent.prototype.isToday = function (date) {
        return moment().isSame(date, 'd');
    };
    Object.defineProperty(GridBarBaseComponent.prototype, "horizonLevelName", {
        get: function () {
            return this.gridService.level.name;
        },
        enumerable: true,
        configurable: true
    });
    GridBarBaseComponent.prototype.setHorizon = function (state) {
        this.gridService.setHorizon(state);
    };
    return GridBarBaseComponent;
}(ControlBase));
export { GridBarBaseComponent };
